// =============================================================================
// File: frontend/src/components/FeedbackModal.jsx
// #83 Phase B — CONVERSATIONAL beta feedback dialog. Drives the conversational-
// capture engine's 'feedback' consumer via /api/beta/feedback/{start,answer,confirm}
// — a short guided chat that refines the tester's words, echo-back confirms, then
// returns the ticket receipt. NOT a static form (reuses the onboarding engine).
// D4S: navy-gradient modal header (design-system rule, no exceptions).
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config';
import { getOrCreateSessionId } from '../utils/auth-helpers';
import { isBetaTelemetryOptedOut, setBetaTelemetryOptOut } from '../utils/telemetry';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
function FeedbackModal({ fetchWithAuth, currentScreen, onClose }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [messages, setMessages] = useState([]);   // {role:'assistant'|'user', text}
  const [input, setInput] = useState('');
  const [phase, setPhase] = useState('loading');   // loading | ask | confirm | done | error
  const [confirmValues, setConfirmValues] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [optOut, setOptOut] = useState(isBetaTelemetryOptedOut());
  const scrollRef = useRef(null);

  const say = (role, text) => setMessages((m) => [...m, { role, text }]);

  const applyAction = (action) => {
    if (!action || action.status === 'error') { setPhase('error'); return; }
    if (action.message && action.status !== 'done') say('assistant', action.message);
    if (action.status === 'confirm') {
      setConfirmValues(action.values || {});
      setPhase('confirm');
    } else if (action.status === 'done') {
      setReceipt(action);
      setPhase('done');
    } else if (action.status === 'handoff' || action.status === 'skipped') {
      setPhase('done');   // graceful end
      if (action.message) say('assistant', action.message);
    } else {
      setPhase('ask');    // ask | clarify | redirect → keep the input open
    }
  };

  const api = async (path, body) => {
    const res = await fetchWithAuth(`${API_BASE}/api/beta/feedback/${path}`, {
      method: 'POST', body: JSON.stringify(body || {}),
    });
    if (!res.ok) throw new Error('Feedback service error');
    return res.json();
  };

  useEffect(() => {
    (async () => {
      try {
        const action = await api('start', {
          screen: currentScreen || window.location.pathname,
          session_id: getOrCreateSessionId(),
        });
        applyAction(action);
      } catch { setPhase('error'); }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, phase]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    say('user', text);
    setInput('');
    setBusy(true);
    try { applyAction(await api('answer', { text })); }
    catch { setPhase('error'); }
    setBusy(false);
  };

  const resolve = async (accept) => {
    setBusy(true);
    if (!accept) say('user', "Let me rephrase that.");
    try { applyAction(await api('confirm', { accept })); }
    catch { setPhase('error'); }
    setConfirmValues(null);
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">🧪 Beta Feedback</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          <div ref={scrollRef} style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
            {phase === 'loading' && <p style={{ color: 'var(--brand-text-muted)' }}>Starting…</p>}
            {messages.map((m, i) => (
              <div key={i} style={{ margin: '8px 0', textAlign: m.role === 'user' ? 'right' : 'left' }}>
                <span style={{
                  display: 'inline-block', padding: '8px 12px', borderRadius: 12, fontSize: 14,
                  maxWidth: '85%', textAlign: 'left',
                  background: m.role === 'user' ? 'linear-gradient(135deg,#0b1f3a,#12305c)' : '#f1f4f9',
                  color: m.role === 'user' ? '#fff' : 'var(--brand-text)',
                }}>{m.text}</span>
              </div>
            ))}
            {phase === 'done' && receipt && (
              <div style={{ marginTop: 10, padding: 12, background: '#f1f4f9', borderRadius: 10 }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Feedback received — thank you!</p>
                <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                  Ticket <strong>{receipt.ticket_number}</strong> — please reference it in any follow-up.
                </p>
              </div>
            )}
            {phase === 'error' && (
              <p style={{ color: 'var(--danger,#c0392b)', fontSize: 14 }}>
                Something went wrong sending that. Please try again in a moment.
              </p>
            )}
          </div>

          {phase === 'confirm' && confirmValues && (
            <div style={{ padding: 10, background: '#fff8e8', borderRadius: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 14 }}>
                <strong>{confirmValues.title || 'Your feedback'}</strong>
                {confirmValues.category && <span> · {confirmValues.category}</span>}
                {confirmValues.severity && <span> · {confirmValues.severity}</span>}
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" disabled={busy} onClick={() => resolve(true)}>Send it</button>
                <button className="btn btn-secondary" disabled={busy} onClick={() => resolve(false)}>Edit</button>
              </div>
            </div>
          )}

          {phase === 'ask' && (
            <div className="form-group" style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" value={input} placeholder="Type your message…" autoFocus
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()} />
              <button className="btn btn-primary" disabled={busy || !input.trim()} onClick={send}>Send</button>
            </div>
          )}

          {(phase === 'done' || phase === 'error') && (
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 12,
            color: 'var(--brand-text-muted)' }} title="Pause anonymous, metadata-only diagnostics (no PHI)">
            <input type="checkbox" checked={optOut}
              onChange={(e) => { setOptOut(e.target.checked); setBetaTelemetryOptOut(e.target.checked); }} />
            Pause anonymous diagnostics (errors + timings, no personal data)
          </label>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;
